/**
 * MOB1 (the 126th) - THE WAY BACK OUT OF A PRINT WINDOW.
 *
 * His phone report, 2026-10-09: "there's no way to exit out of it or get out of it once I have like the PDF document"
 * (the LOI maker and the brochure). Every Print / PDF writes its document into a window of its own, and
 * ⭐⭐⭐ REPRODUCED on an iPhone simulator (iPhone 17 Pro, iOS 26) with the LOI's own code verbatim: as a HOME-SCREEN APP
 * that window is the document full screen with NO control of any kind - no address bar, no back arrow, the edge swipe
 * does nothing - so the only way out was to kill the app. In Safari it is a new tab whose back arrow is dead.
 *
 * ⭐ THE FIX IS MEASURED TOO (`docs/planning/2026-10-09-mob1-repro/`): a bar written into that window, whose button calls
 *   `window.close()`, returned to the app 4 ms later with the app's page untouched (an open dialog keeps its state).
 * ⛔ NOT a print from a hidden in-page frame: that is reported blank on iPhone Safari, and today's print WORKS - he got
 *   his PDF. What was missing is only the way back, so that is all this adds.
 * ⭐ Only a window the app WRITES INTO is chromeless. One pointed at a URL (a signed PDF, a blob PDF, GHL) gets iOS's own
 *   sheet with a close button - measured - so Renewals, the Deal Room and Export PDF need nothing.
 */

/** The bar's element id in a print window. */
export const WAY_BACK_ID = 'apex-print-back';

/**
 * ⚠️ SIZED FOR WHAT A PHONE DOES TO HIS DOCUMENTS. The LOI, the proposal and the brochure carry no viewport tag, so a
 *   phone lays them out wide and shrinks the page: a 15px label would be a few points tall. On a phone-sized DEVICE the
 *   bar scales with the page instead (vw); on a desk it is a plain 15px. `max-device-width` reads the device, never the
 *   shrunken layout - which is the whole reason it is used.
 * ⛔ `display: none` in print: the bar is for the screen only, and the print preview was checked without it.
 */
const WAY_BACK_CSS =
  `#${WAY_BACK_ID}{position:sticky;top:0;z-index:2147483647;display:flex;flex-wrap:wrap;gap:.6em;align-items:center;` +
  'padding:.6em .8em;background:#fff;border-bottom:1px solid #ddd;color:#333;' +
  'font:600 15px/1.2 -apple-system,system-ui,"Segoe UI",sans-serif}' +
  `@media (max-device-width:500px){#${WAY_BACK_ID}{font-size:max(17px,4.5vw)}}` +
  `#${WAY_BACK_ID} button{font:inherit;padding:.55em 1em;border-radius:.6em;border:1px solid #CE011F;` +
  'background:#fff5f5;color:#CE011F;cursor:pointer}' +
  `#${WAY_BACK_ID} [data-way-back-hint]{flex-basis:100%;font-weight:400;font-size:.8em}` +
  `@media print{#${WAY_BACK_ID}{display:none!important}}`;

/**
 * Adds the bar to a document already written into a print window: "Back to Apex" closes the window, "Print again"
 * prints it. Added once, however often it is asked for. Answers whether the bar is in the document.
 *
 * ⛔ IT NEVER THROWS: THE WAY BACK IS NEVER AT THE COST OF THE PRINT. A caller prints straight after this; if the bar
 *   cannot be added the document still prints - losing the print to a convenience would be a worse defect than the
 *   one this fixes. (The tour report's first run of its tests showed exactly that: the bar threw and nothing printed.)
 * ⛔ THE HANDLERS ARE ATTRIBUTES, so they are compiled in the print window itself. A listener handed over from the
 *   app's page would depend on that page still running behind a window that covers it.
 * ⚠️ The hint is UNMEASURED insurance, said plainly: in every mode that could be measured (Safari and the home-screen
 *   app, iOS 26) the window closed. Chrome on an iPhone could not be installed on the simulator, so if a browser ever
 *   refuses the close, the bar says what to do instead of sitting there dead.
 */
export const addWayBack = (doc: Document): boolean => {
  try {
    return placeWayBack(doc);
  } catch {
    return false;
  }
};

const placeWayBack = (doc: Document): boolean => {
  if (doc.getElementById(WAY_BACK_ID)) return true;

  const style = doc.createElement('style');
  style.setAttribute('data-apex-print-back', '');
  style.textContent = WAY_BACK_CSS;

  const bar = doc.createElement('div');
  bar.id = WAY_BACK_ID;

  const back = doc.createElement('button');
  back.type = 'button';
  back.textContent = '‹ Back to Apex';
  back.setAttribute(
    'onclick',
    'window.close();setTimeout(function(){var h=document.querySelector("[data-way-back-hint]");if(h)h.hidden=false},700)',
  );

  const again = doc.createElement('button');
  again.type = 'button';
  again.textContent = 'Print again';
  again.setAttribute('onclick', 'window.print()');

  const hint = doc.createElement('span');
  hint.setAttribute('data-way-back-hint', '');
  hint.hidden = true;
  hint.textContent = 'If this page stays open, close this tab to return to Apex.';

  bar.append(back, again, hint);
  (doc.head ?? doc.documentElement).appendChild(style);
  doc.body.insertBefore(bar, doc.body.firstChild);
  return true;
};

/**
 * Print / PDF: the document in a window of its own, printed - and carrying the way back.
 *
 * ⛔ CALL IT INSIDE THE TAP, with nothing awaited first: a browser lets a window open only as the direct result of his
 *   press. Answers the window, or null when the browser refused to open one.
 */
export const printInOwnWindow = (html: string): Window | null => {
  const w = window.open('', '_blank');
  if (!w) return null;
  w.document.write(html);
  w.document.close();
  addWayBack(w.document);
  w.focus();
  w.print();
  return w;
};
